<script lang="ts" setup>
import { OptionsProps } from "~/components/Filters/Utils/Modals/Options.props"

const props = withDefaults(defineProps<OptionsProps>(), {
  searchItem: ""
})

const emit =
  defineEmits<{
    (e: "onClose"): void
    (e: "onDeleteAll"): void
    (e: "onSelectAll"): void
    (e: "onApply"): void
  }>()

const { t } = useI18n()
const { isOpen, switchState } = useUIState(props.name)

let overflowRefreshFn: (() => void) | null = null
const searchLabel = `${t("filters.utils.modals.options.which")} ${
  props.searchItem
} ${t("filters.utils.modals.options.areYouSearchingFor")}`

const headerAction = computed(() => (props.nOptions > 0 ? "reset" : "none"))

const handleContentMounted = (fn: () => void) =>
  (overflowRefreshFn = useDebounceFn(fn, 100))

const handleClose = () => {
  switchState(false)
  emit("onClose")
}

const handleApply = () => {
  switchState(false)
  emit("onApply")
}

watch(isOpen, () => overflowRefreshFn?.())

const { event, sendEvent } = useGAEvent(
  `track_select_content_${props.name}_`,
  "custom_event"
)

const onClickedFilter = (filter: {label: string}, category: string) => {
  event.value = {
    event: "select_content",
    action: "filter",
    category: category.toLowerCase(),
    label: filter.label
  }
  sendEvent({ clearEcommerce: false })
}

const { nbHits } = useSwiftSearchHelper()
</script>

<template>
  <FiltersUtilsModalWrapper
    :name="name"
    :header="{
      title,
      action: headerAction
    }"
    open-from="right"
    @onSelectAll="emit('onSelectAll')"
    @onDeleteAll="emit('onDeleteAll')"
    @onClose="handleClose"
  >
    <FiltersUtilsModalContent
      :btn="{
        text: $t('filters.utils.common.saveFilter'),
        nResults: 120
      }"
      :btn-shown="nOptions > 0"
      @onMounted="handleContentMounted"
      @onClick="handleApply"
    >
      <AisRefinementList :attribute="facetName" searchable :limit="20">
        <template v-slot="{ items, refine, searchForItems }">
          <div class="options-modal -mx-4 space-y-2 pt-4">
            <div class="options-modal__search px-4 py-2">
              <SearchSearchbar
                name="modal-search"
                :label="searchLabel"
                :placeholder="searchLabel"
                @update:model-value="searchForItems"
              />
            </div>

            <div class="options-modal__options pb-16">
              <FiltersUtilsOption
                v-for="item of items"
                :name="item.value"
                :checked="item.isRefined"
                @clicked="
                  () => {
                    onClickedFilter(item, title)
                    refine(item.value)
                  }
                "
                :text="item.label"
                :n-results="item.count"
                class="px-4"
              />
            </div>
            <div
              class="
                elevation-fixed-button
                fixed
                bottom-0
                right-0
                w-full
                bg-white
                p-4
              "
            >
              <UtilsButton
                @click="handleClose"
                :text="
                  $t('filters.utils.common.showResults', {
                    number: nbHits
                  })
                "
                class="w-full"
              />
            </div>
          </div>
        </template>
      </AisRefinementList>
    </FiltersUtilsModalContent>
  </FiltersUtilsModalWrapper>
</template>

<style lang="scss" scoped>

.elevation-fixed-button {
  box-shadow: 0px -2px 12px 0px rgba(0, 0, 0, 0.08);
  -webkit-overflow-scrolling: touch;
}

</style>
